import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UserMinus, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchChurnFeedback, fetchChurnFeedbackDetail } from '../api/analyticsApi';
import EngagementListModal from './EngagementListModal';

// Clickable horizontal bar list — each reason drills into the submissions.
const BarList = ({ items, color, onItemClick }) => {
    const max = Math.max(1, ...items.map((i) => i.count || 0));
    if (items.length === 0) {
        return <p className="text-xs text-gray-400 py-4 text-center">No feedback in this range yet.</p>;
    }
    return (
        <div className="space-y-2.5">
            {items.map((item, idx) => {
                const clickable = !!onItemClick && (item.count || 0) > 0;
                return (
                    <button
                        type="button"
                        key={item.reason ?? idx}
                        onClick={clickable ? () => onItemClick(item) : undefined}
                        disabled={!clickable}
                        className={`flex items-center gap-3 w-full text-left rounded-lg px-1 py-0.5 ${
                            clickable ? 'hover:bg-gray-50 cursor-pointer' : 'cursor-default'
                        }`}
                    >
                        <span className="text-xs text-gray-600 w-40 flex-shrink-0 truncate" title={item.label}>
                            {item.label}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div className="h-4 rounded-full"
                                 style={{ width: `${Math.max(6, ((item.count || 0) / max) * 100)}%`, background: color }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-900 w-16 text-right">
                            {(item.count || 0).toLocaleString()}
                            {item.percentage != null && (
                                <span className="text-gray-400 font-normal"> · {item.percentage}%</span>
                            )}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

const FLOWS = [
    { key: '', label: 'All' },
    { key: 'deactivate', label: 'Deactivations' },
    { key: 'unsubscribe', label: 'Unsubscribes' },
];

/**
 * "Why buyers leave" — reasons captured when buyers deactivate their account or
 * unsubscribe from emails (AccountFeedback). Multi-select, so percentages are of
 * total submissions. Click a reason to see the individual submissions + comments.
 */
const ChurnFeedbackPanel = ({ startDate, endDate }) => {
    const [flow, setFlow] = useState('');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const loadTokenRef = useRef(0);
    const [listModal, setListModal] = useState({ open: false, loading: false, title: '', subtitle: '', rows: [] });

    useEffect(() => {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        fetchChurnFeedback(startDate, endDate, flow)
            .then((res) => { if (myToken === loadTokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== loadTokenRef.current) return;
                console.error('Churn feedback load failed:', err);
                toast.error('Failed to load churn feedback');
            })
            .finally(() => { if (myToken === loadTokenRef.current) setLoading(false); });
    }, [startDate, endDate, flow]);

    const openReason = useCallback(async (item) => {
        setListModal({ open: true, loading: true, title: `“${item.label}”`, subtitle: 'Buyers who gave this reason', rows: [] });
        try {
            const res = await fetchChurnFeedbackDetail(startDate, endDate, item.reason, flow);
            setListModal((m) => ({ ...m, loading: false, rows: res?.rows || [] }));
        } catch (err) {
            console.error('Churn drill-down failed:', err);
            toast.error('Failed to load submissions');
            setListModal((m) => ({ ...m, loading: false, rows: [] }));
        }
    }, [startDate, endDate, flow]);

    if (loading && !data) {
        return (
            <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading churn feedback…
            </div>
        );
    }

    const byFlow = data?.by_flow || {};
    return (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
                        <UserMinus className="w-4 h-4 text-[#dc2626]" /> Why Buyers Leave
                    </h3>
                    <p className="text-xs text-gray-400 mb-4">
                        {data?.total_submissions ?? 0} submission{(data?.total_submissions ?? 0) === 1 ? '' : 's'}
                        {' · '}{byFlow.deactivate ?? 0} deactivation{(byFlow.deactivate ?? 0) === 1 ? '' : 's'}
                        {' · '}{byFlow.unsubscribe ?? 0} unsubscribe{(byFlow.unsubscribe ?? 0) === 1 ? '' : 's'}
                        {' · click a reason for the list'}
                    </p>
                </div>
                <div className="inline-flex rounded-lg border border-gray-200 p-0.5 bg-gray-50">
                    {FLOWS.map((f) => (
                        <button
                            key={f.key || 'all'}
                            type="button"
                            onClick={() => setFlow(f.key)}
                            className={`px-2.5 py-1 rounded-md text-xs font-semibold transition-colors ${
                                flow === f.key ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'
                            }`}
                        >
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            <BarList items={data?.by_reason || []} color="#dc2626" onItemClick={openReason} />

            <EngagementListModal
                isOpen={listModal.open}
                onClose={() => setListModal((m) => ({ ...m, open: false }))}
                title={listModal.title}
                subtitle={listModal.subtitle}
                loading={listModal.loading}
                rows={listModal.rows}
                variant="churn"
            />
        </div>
    );
};

export default ChurnFeedbackPanel;
